import React, { useEffect, useMemo } from 'react';
import {
  renderTextField,
  renderSelectField,
  renderEmailField,
  renderPhoneFieldWithPermanentWarn,
  phoneParser,
  phoneFormatter,
} from '../../../../utils/redux-form-helper';
import { Field, FieldArray } from 'redux-form';
import { Col, Row } from 'react-bootstrap';
import { Divider } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  validatePhoneNumber,
  validateEmail,
} from '../../../../utils/validations';
import deleteConfirmation from '../../../../components/DeleteConfirmation';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import parentMessages from '../messages';
import primaryContactMessages from '../PrimaryContactInformation/messages';

const DeleteButton = ({ onClick }) => (
  <div className="row-remove-btn">
    <a onClick={onClick} style={{ cursor: 'pointer' }}>
      <i className="et-trash" />
    </a>
  </div>
);

const PhoneFields = ({ fields, field, intl, change }) => {
  const maxFields = 3;
  const phoneTypes = [
    { value: 'mobile', text: intl.formatMessage(messages.phoneTypeMobile) },
    { value: 'work', text: intl.formatMessage(messages.phoneTypeWork) },
    { value: 'other', text: intl.formatMessage(messages.phoneTypeOther) },
  ];

  // Memoize validation function to prevent infinite re-renders
  const validatePhone = useMemo(() => {
    return (value) => {
      if (value && !validatePhoneNumber(value)) {
        return intl.formatMessage(parentMessages.invalidPhoneNumber);
      }
      return undefined;
    };
  }, [intl]);

  // Ensure first item is always primary when fields change
  useEffect(() => {
    if (fields.length > 0) {
      // Check if first item needs to be updated to primary
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.phones[0].isPrimary`, true);
      }

      // Ensure all other items are not primary
      for (let i = 1; i < fields.length; i++) {
        const currentField = fields.get(i);
        if (currentField && currentField.isPrimary) {
          change(`${field}.phones[${i}].isPrimary`, false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const addPhone = () => {
    if (fields.length < maxFields) {
      fields.push({
        phoneNumber: '',
        phoneType: 'mobile',
        isPrimary: fields.length === 0, // First item is primary
        isValidated: false,
      });
    }
  };

  const handleRemove = (index) => {
    fields.remove(index);
    // After removal, ensure first item (if exists) is primary
    if (fields.length > 0 && index === 0) {
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.phones[0].isPrimary`, true);
      }
    }
  };

  return (
    <div>
      {fields.map((phone, index) => (
        <div key={index}>
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}
          >
            <div style={{ flex: '5' }}>
              <Field
                name={`${phone}.phoneNumber`}
                component={renderPhoneFieldWithPermanentWarn}
                parse={phoneParser}
                format={phoneFormatter}
                placeholder={`Phone ${index + 1}`}
                validate={validatePhone}
              />
            </div>
            <div style={{ flex: '5' }}>
              <Field
                name={`${phone}.phoneType`}
                component={renderSelectField}
                options={phoneTypes}
              />
              {/* Invisible placeholder to maintain alignment when validation errors appear */}
              <div style={{ height: '21px', visibility: 'hidden' }}>&nbsp;</div>
            </div>
            <div
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                height: '34px',
              }}
            >
              <DeleteButton onClick={() => handleRemove(index)} />
            </div>
          </div>
        </div>
      ))}
      {fields.length < maxFields && (
        <div>
          <a className="btn-text" onClick={addPhone}>
            {intl.formatMessage(primaryContactMessages.addPhone)}
          </a>
        </div>
      )}
    </div>
  );
};

const EmailFields = ({ fields, field, intl, change }) => {
  const maxFields = 3;
  const emailTypes = [
    { value: 'personal', text: intl.formatMessage(messages.emailTypePersonal) },
    { value: 'work', text: intl.formatMessage(messages.emailTypeWork) },
    { value: 'other', text: intl.formatMessage(messages.emailTypeOther) },
  ];

  // Memoize validation function to prevent infinite re-renders
  const validateEmailField = useMemo(() => {
    return (value) => {
      if (value && !validateEmail(value)) {
        return intl.formatMessage(parentMessages.invalidEmailAddress);
      }
      return undefined;
    };
  }, [intl]);

  // Ensure first item is always primary when fields change
  useEffect(() => {
    if (fields.length > 0) {
      // Check if first item needs to be updated to primary
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.emails[0].isPrimary`, true);
      }

      // Ensure all other items are not primary
      for (let i = 1; i < fields.length; i++) {
        const currentField = fields.get(i);
        if (currentField && currentField.isPrimary) {
          change(`${field}.emails[${i}].isPrimary`, false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const addEmail = () => {
    if (fields.length < maxFields) {
      fields.push({
        emailAddress: '',
        emailType: 'personal',
        isPrimary: fields.length === 0, // First item is primary
        isValidated: false,
      });
    }
  };

  const handleRemove = (index) => {
    fields.remove(index);
    // After removal, ensure first item (if exists) is primary
    if (fields.length > 0 && index === 0) {
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.emails[0].isPrimary`, true);
      }
    }
  };

  return (
    <div>
      {fields.map((email, index) => (
        <div key={index}>
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}
          >
            <div style={{ flex: '5' }}>
              <Field
                name={`${email}.emailAddress`}
                component={renderEmailField}
                maxLength="70"
                placeholder={`Email ${index + 1}`}
                validate={validateEmailField}
              />
            </div>
            <div style={{ flex: '5' }}>
              <Field
                name={`${email}.emailType`}
                component={renderSelectField}
                options={emailTypes}
              />
              {/* Invisible placeholder to maintain alignment when validation errors appear */}
              <div style={{ height: '21px', visibility: 'hidden' }}>&nbsp;</div>
            </div>
            <div
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                height: '34px',
              }}
            >
              <DeleteButton onClick={() => handleRemove(index)} />
            </div>
          </div>
        </div>
      ))}
      {fields.length < maxFields && (
        <div>
          <a className="btn-text" onClick={addEmail}>
            {intl.formatMessage(primaryContactMessages.addEmail)}
          </a>
        </div>
      )}
    </div>
  );
};

type ComponentProps = {
  intl: any,
  adult: any,
  onDelete: Function,
  suffixes: Array<Object>,
  relationships: Array<Object>,
  change: Function,
};

const AdultIndividual = ({
  intl,
  adult,
  onDelete,
  suffixes,
  relationships,
  change,
}: ComponentProps) => {
  const deleteItemCallback = (options) => {
    deleteConfirmation(
      intl.formatMessage(parentMessages.deleteItemConfirmation),
      {
        intl,
      },
    )
      .then(() => onDelete(options))
      .catch(() => {});
  };

  return (
    <>
      {/* Header Row with Labels */}
      <Row>
        <div className="labels-head container-of-inputs">
          <Col xs={6} sm={2}>
            <label>
              <FormattedMessage {...messages.relationship} /> *
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.firstName} /> *
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.middleName} />
            </label>
          </Col>
          <Col xs={6} sm={2}>
            <label>
              <FormattedMessage {...messages.lastName} /> *
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.suffix} />
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.preferredName} />
            </label>
          </Col>
        </div>
        <div className="container-of-trash">
          <div className="row-remove-btn">
            <label>
              <FormattedMessage {...messages.delete} />
            </label>
          </div>
        </div>
      </Row>

      {/* Input Row */}
      <Row className="adults-row">
        <div className="container-of-inputs">
          <Col xs={6} sm={2}>
            <Field
              selectClassNames="input-lg"
              name={`${adult}.relationshipId`}
              component={renderSelectField}
              options={relationships}
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              className="input-lg"
              name={`${adult}.firstName`}
              component={renderTextField}
              maxLength="35"
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              className="input-lg"
              name={`${adult}.middleName`}
              component={renderTextField}
              maxLength="35"
            />
          </Col>
          <Col xs={6} sm={2}>
            <Field
              className="input-lg"
              name={`${adult}.lastName`}
              component={renderTextField}
              maxLength="35"
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              selectClassNames="input-lg"
              name={`${adult}.suffixId`}
              component={renderSelectField}
              options={suffixes}
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              className="input-lg"
              name={`${adult}.preferredName`}
              component={renderTextField}
              maxLength="35"
            />
          </Col>
        </div>
        <div className="container-of-trash">
          <div className="row-remove-btn">
            <a onClick={deleteItemCallback}>
              <i className="et-trash" />
            </a>
          </div>
        </div>
      </Row>

      {/* Phone and Email Field Arrays */}
      <Row>
        <div className="container-of-inputs">
          <Col xs={5} sm={5}>
            <label>
              <FormattedMessage {...messages.phone} />
            </label>
          </Col>
          <Col xs={5} sm={5}>
            <label>
              <FormattedMessage {...messages.email} />
            </label>
          </Col>
        </div>
      </Row>
      <Row>
        <div className="container-of-inputs">
          <Col xs={5} sm={5}>
            <FieldArray
              name={`${adult}.phones`}
              component={PhoneFields}
              field={adult}
              intl={intl}
              change={change}
            />
          </Col>
          <Col xs={5} sm={5}>
            <FieldArray
              name={`${adult}.emails`}
              component={EmailFields}
              field={adult}
              intl={intl}
              change={change}
            />
          </Col>
        </div>
      </Row>
      <Divider variant="dashed" style={{ marginBottom: 12 }} />
    </>
  );
};

export default AdultIndividual;
